import type { AppIconKey } from "~/components/app-icon";
import * as m from "~/paraglide/messages.js";

export const TASK_KEYS = [
  "support",
  "sales",
  "operations",
  "marketing",
  "management",
] as const;

export type TaskKey = (typeof TASK_KEYS)[number];

export interface WorkbenchSource {
  detail: string;
  icon: AppIconKey;
  name: string;
}

/**
 * The artifact each task prepares. A discriminated union rather than a string of
 * HTML: adding a sixth kind is then a compile error until the renderer handles
 * it, which is the property the design's `innerHTML` swap could not give us.
 */
export type WorkbenchArtifact =
  | { kind: "chat" }
  | { kind: "email" }
  | { kind: "order" }
  | { kind: "report" }
  | { kind: "brief" };

export interface WorkbenchTask {
  approvedState: string;
  artifact: WorkbenchArtifact;
  /** Named by the tool, so it is not translated. */
  channel: string;
  context: string;
  description: string;
  icon: AppIconKey;
  key: TaskKey;
  label: string;
  /**
   * Operations is the task that deliberately cannot complete: the order is
   * missing a required field, so the control flags it to a person instead of
   * approving it. That asymmetry is the point of the section.
   */
  needsPerson?: boolean;
  result: string;
  sources: WorkbenchSource[];
  title: [string, string];
}

export function getWorkbenchTasks(): Record<TaskKey, WorkbenchTask> {
  return {
    management: {
      approvedState: m.wb_management_approved(),
      artifact: { kind: "brief" },
      channel: "Slack",
      context: m.wb_management_context(),
      description: m.wb_management_description(),
      icon: "slack",
      key: "management",
      label: m.home_task_management_label(),
      result: m.wb_management_result(),
      sources: [
        {
          detail: m.wb_management_src_1_detail(),
          icon: "hubspot",
          name: m.wb_management_src_1_name(),
        },
        {
          detail: m.wb_management_src_2_detail(),
          icon: "googlesheets",
          name: m.wb_management_src_2_name(),
        },
        {
          detail: m.wb_management_src_3_detail(),
          icon: "notion",
          name: m.wb_management_src_3_name(),
        },
      ],
      title: [m.wb_management_title_1(), m.wb_management_title_2()],
    },
    marketing: {
      approvedState: m.wb_marketing_approved(),
      artifact: { kind: "report" },
      channel: "Google Analytics",
      context: m.wb_marketing_context(),
      description: m.wb_marketing_description(),
      icon: "googleanalytics",
      key: "marketing",
      label: m.home_task_marketing_label(),
      result: m.wb_marketing_result(),
      sources: [
        {
          detail: m.wb_marketing_src_1_detail(),
          icon: "googleanalytics",
          name: m.wb_marketing_src_1_name(),
        },
        {
          detail: m.wb_marketing_src_2_detail(),
          icon: "googlesheets",
          name: m.wb_marketing_src_2_name(),
        },
        {
          detail: m.wb_marketing_src_3_detail(),
          icon: "googledocs",
          name: m.wb_marketing_src_3_name(),
        },
      ],
      title: [m.wb_marketing_title_1(), m.wb_marketing_title_2()],
    },
    operations: {
      approvedState: m.wb_operations_approved(),
      artifact: { kind: "order" },
      channel: "Google Sheets",
      context: m.wb_operations_context(),
      description: m.wb_operations_description(),
      icon: "googlesheets",
      key: "operations",
      label: m.home_task_operations_label(),
      needsPerson: true,
      result: m.wb_operations_result(),
      sources: [
        {
          detail: m.wb_operations_src_1_detail(),
          icon: "gmail",
          name: m.wb_operations_src_1_name(),
        },
        {
          detail: m.wb_operations_src_2_detail(),
          icon: "googlesheets",
          name: m.wb_operations_src_2_name(),
        },
        {
          detail: m.wb_operations_src_3_detail(),
          icon: "notion",
          name: m.wb_operations_src_3_name(),
        },
      ],
      title: [m.wb_operations_title_1(), m.wb_operations_title_2()],
    },
    sales: {
      approvedState: m.wb_sales_approved(),
      artifact: { kind: "email" },
      channel: "Gmail",
      context: m.wb_sales_context(),
      description: m.wb_sales_description(),
      icon: "gmail",
      key: "sales",
      label: m.home_task_sales_label(),
      result: m.wb_sales_result(),
      sources: [
        {
          detail: m.wb_sales_src_1_detail(),
          icon: "gmail",
          name: m.wb_sales_src_1_name(),
        },
        {
          detail: m.wb_sales_src_2_detail(),
          icon: "hubspot",
          name: m.wb_sales_src_2_name(),
        },
        {
          detail: m.wb_sales_src_3_detail(),
          icon: "googlecalendar",
          name: m.wb_sales_src_3_name(),
        },
      ],
      title: [m.wb_sales_title_1(), m.wb_sales_title_2()],
    },
    support: {
      approvedState: m.wb_support_approved(),
      artifact: { kind: "chat" },
      channel: "WhatsApp Business",
      context: m.wb_support_context(),
      description: m.wb_support_description(),
      icon: "whatsapp",
      key: "support",
      label: m.home_task_support_label(),
      result: m.wb_support_result(),
      sources: [
        {
          detail: m.wb_support_src_1_detail(),
          icon: "googlesheets",
          name: m.wb_support_src_1_name(),
        },
        {
          detail: m.wb_support_src_2_detail(),
          icon: "googlecalendar",
          name: m.wb_support_src_2_name(),
        },
        {
          detail: m.wb_support_src_3_detail(),
          icon: "googledocs",
          name: m.wb_support_src_3_name(),
        },
      ],
      title: [m.wb_support_title_1(), m.wb_support_title_2()],
    },
  };
}

export function getTaskArea(key: TaskKey): string {
  const areas: Record<TaskKey, string> = {
    management: m.home_task_management_area(),
    marketing: m.home_task_marketing_area(),
    operations: m.home_task_operations_area(),
    sales: m.home_task_sales_area(),
    support: m.home_task_support_area(),
  };
  return areas[key];
}
